import { useCallback, useEffect, useRef, useState } from 'react'
import * as SpeechSDK from 'microsoft-cognitiveservices-speech-sdk'
import type { SpeechToken } from '@/services/liveInterviewPublicApi'

/**
 * Speaks the AI interviewer's lines out loud and reports whether it's
 * currently talking (so the UI can show a "speaking" indicator on the
 * avatar tile, and — far more importantly — so LiveInterviewRoomPage knows
 * exactly how long to keep the candidate's mic gated silent; see its own
 * aiMicSuppressed doc comment).
 *
 * Primary voice: Azure Neural TTS, through the same Speech SDK + short-lived
 * token live transcription already uses (`getToken`). The voice/style/rate
 * are server-configured (AI_INTERVIEWER_TTS_* in core/config.py, carried on
 * the speech-token response), so switching to another neural or HD voice is
 * a .env edit. Audio plays through the SDK's SpeakerAudioDestination, whose
 * onAudioEnd fires when playback has genuinely finished — an exact signal
 * for the mic gate, unlike the browser engine's flaky per-utterance events.
 *
 * Fallback: the browser's built-in SpeechSynthesis, used whenever Azure is
 * unavailable (no token, synthesis error) so the interview never goes silent.
 * Its `speaking` state is driven by POLLING window.speechSynthesis.speaking —
 * proven necessary: Chrome's onstart/onend can simply never fire, which once
 * let the AI's own voice slip past the mic gate and get transcribed as the
 * candidate's words.
 *
 * Same {speak, stop, speaking, supported} interface either way, so the room
 * doesn't care which engine spoke — and the planned Azure TTS Avatar can
 * implement it too.
 */

const DEFAULT_TOKEN_REFRESH_SECONDS = 480

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')
}

function buildSsml(text: string, voice: string, style?: string, rate?: string): string {
  const lang = /^[a-z]{2}-[A-Z]{2}/.exec(voice)?.[0] || 'en-US'
  let body = escapeXml(text)
  if (rate) body = `<prosody rate="${escapeXml(rate)}">${body}</prosody>`
  if (style) body = `<mstts:express-as style="${escapeXml(style)}">${body}</mstts:express-as>`
  return (
    `<speak version="1.0" xmlns="http://www.w3.org/2001/10/synthesis" ` +
    `xmlns:mstts="https://www.w3.org/2001/mstts" xml:lang="${lang}">` +
    `<voice name="${escapeXml(voice)}">${body}</voice></speak>`
  )
}

export function useAiInterviewerVoice(
  getToken?: () => Promise<SpeechToken>,
  /** Called with a capturable MediaStream of each Azure-voiced line as it
   *  starts playing — lets the call recording include the AI's voice (it
   *  otherwise plays straight to the speakers and never reaches the mix).
   *  The browser-voice fallback can't be captured, so it never fires there. */
  onAudioStream?: (stream: MediaStream) => void,
  /** How far the AI has got through the line it's speaking: `spokenChars`
   *  characters of `text` have been heard so far. Lets the caption highlight
   *  the sentence being spoken in sync with the voice. Azure: word-boundary
   *  timings matched against the audio element's real playback position.
   *  Browser voice: its own onboundary events. Called with text.length once
   *  the line finishes; may never be called if the engine reports no
   *  boundaries. */
  onSpeechProgress?: (text: string, spokenChars: number) => void,
) {
  const [speaking, setSpeaking] = useState(false)
  const onAudioStreamRef = useRef(onAudioStream)
  onAudioStreamRef.current = onAudioStream
  const onSpeechProgressRef = useRef(onSpeechProgress)
  onSpeechProgressRef.current = onSpeechProgress
  const reportProgress = (text: string, spokenChars: number) => {
    if (onSpeechProgressRef.current) onSpeechProgressRef.current(text, spokenChars)
  }
  const browserSupported = typeof window !== 'undefined' && !!window.speechSynthesis
  const supported = browserSupported || !!getToken
  const voiceRef = useRef<SpeechSynthesisVoice | null>(null)
  const getTokenRef = useRef(getToken)
  getTokenRef.current = getToken
  const tokenRef = useRef<{ data: SpeechToken; fetchedAt: number } | null>(null)
  // Playing Azure audio, if any - stopped before every new line.
  const playerRef = useRef<SpeechSDK.SpeakerAudioDestination | null>(null)
  const azureActiveRef = useRef(false)
  // Bumped on every speak/stop so a superseded line's callbacks are ignored.
  const generationRef = useRef(0)

  useEffect(() => {
    if (!browserSupported) return
    // Voice list loads asynchronously in most browsers (fires
    // 'voiceschanged' once ready) — prefer a natural-sounding English voice.
    const pickVoice = () => {
      const voices = window.speechSynthesis.getVoices()
      voiceRef.current =
        voices.find((v) => /en-US|en-GB/i.test(v.lang) && /Natural|Neural|Online/i.test(v.name))
        || voices.find((v) => /en-US|en-GB/i.test(v.lang))
        || voices[0]
        || null
    }
    pickVoice()
    window.speechSynthesis.addEventListener('voiceschanged', pickVoice)
    // Source of truth for `speaking` while the BROWSER engine talks (see
    // docstring); Azure playback sets azureActiveRef itself.
    const poll = setInterval(() => {
      setSpeaking(azureActiveRef.current || window.speechSynthesis.speaking)
    }, 100)
    return () => {
      window.speechSynthesis.removeEventListener('voiceschanged', pickVoice)
      clearInterval(poll)
    }
  }, [browserSupported])

  const fetchToken = useCallback(async (): Promise<SpeechToken | null> => {
    if (!getTokenRef.current) return null
    const cached = tokenRef.current
    const refreshMs = (cached?.data.token_refresh_seconds || DEFAULT_TOKEN_REFRESH_SECONDS) * 1000
    if (cached && Date.now() - cached.fetchedAt < refreshMs) return cached.data
    try {
      const data = await getTokenRef.current()
      tokenRef.current = { data, fetchedAt: Date.now() }
      return data
    } catch {
      return null
    }
  }, [])

  const stopAzure = () => {
    azureActiveRef.current = false
    try { playerRef.current?.pause() } catch { /* already stopped */ }
    try { playerRef.current?.close() } catch { /* already closed */ }
    playerRef.current = null
  }

  const speakBrowser = (text: string, generation: number): Promise<void> => {
    if (!browserSupported) return Promise.resolve()
    return new Promise((resolve) => {
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)
      if (voiceRef.current) utterance.voice = voiceRef.current
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        clearInterval(pollDone)
        clearTimeout(hardCap)
        if (generation === generationRef.current) {
          setSpeaking(false)
          reportProgress(text, text.length)
        }
        resolve()
      }
      utterance.onstart = () => setSpeaking(true)
      // Fires as each word starts being spoken - report up to the end of that word.
      utterance.onboundary = (event: SpeechSynthesisEvent) => {
        if (generation !== generationRef.current) return
        let end = event.charIndex + (event.charLength || 0)
        if (!event.charLength) {
          const nextSpace = text.slice(event.charIndex).search(/\s/)
          end = nextSpace < 0 ? text.length : event.charIndex + nextSpace
        }
        reportProgress(text, end)
      }
      utterance.onend = finish
      utterance.onerror = finish
      // Safety nets for the browser engine's unreliable onend: poll its own
      // idle flags, plus an absolute cap sized to the text in case
      // speechSynthesis.speaking itself wedges true (a known Chrome bug).
      const pollDone = setInterval(() => {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) finish()
      }, 100)
      const hardCap = setTimeout(finish, Math.min(60000, Math.max(4000, text.length * 120)))
      window.speechSynthesis.speak(utterance)
    })
  }

  // Resolves with true once Azure finished playing the line, false if Azure
  // couldn't speak it at all (caller falls back to the browser voice).
  const speakAzure = (text: string, token: SpeechToken, generation: number): Promise<boolean> =>
    new Promise((resolve) => {
      let settled = false
      let started = false
      // Speech-progress tracking (see onSpeechProgress): each word boundary's
      // audio time + where that word ends in `text`, polled against the
      // player's real playback position.
      const boundaries: Array<{ atMs: number; endChar: number }> = []
      let boundaryCursor = 0
      let lastReported = -1
      let progressPoll: ReturnType<typeof setInterval> | null = null
      const stopProgressPoll = () => {
        if (progressPoll) { clearInterval(progressPoll); progressPoll = null }
      }
      const finish = (ok: boolean) => {
        if (settled) return
        settled = true
        clearTimeout(hardCap)
        stopProgressPoll()
        if (generation === generationRef.current) {
          azureActiveRef.current = false
          setSpeaking(false)
          if (ok && started) reportProgress(text, text.length)
        }
        resolve(ok)
      }
      try {
        const config = SpeechSDK.SpeechConfig.fromAuthorizationToken(token.token, token.region)
        const voice = token.tts_voice || 'en-US-AvaMultilingualNeural'
        config.speechSynthesisVoiceName = voice
        const player = new SpeechSDK.SpeakerAudioDestination()
        player.onAudioStart = () => {
          started = true
          // Poll the real playback position and report the last word heard by
          // then (only when it actually moves).
          progressPoll = setInterval(() => {
            if (settled || generation !== generationRef.current) { stopProgressPoll(); return }
            const nowMs = player.currentTime * 1000
            if (nowMs < 0) return
            let spoken = 0
            for (const b of boundaries) {
              if (b.atMs > nowMs) break
              spoken = b.endChar
            }
            if (spoken !== lastReported) {
              lastReported = spoken
              reportProgress(text, spoken)
            }
          }, 120)
          // Tap the SDK's own <audio> element so the recording hears the AI.
          // The SDK fires onAudioStart BEFORE it calls audio.play(), when the
          // captured stream usually has no audio track yet (the browser adds
          // it once the element has loaded media) — so hand the stream over
          // as soon as its track appears instead of dropping it.
          try {
            const el = player.internalAudio as HTMLAudioElement & {
              captureStream?: () => MediaStream
              mozCaptureStream?: () => MediaStream
            }
            if (!el) return
            const stream = el.captureStream ? el.captureStream() : el.mozCaptureStream ? el.mozCaptureStream() : null
            if (!stream) return
            let delivered = false
            const deliver = () => {
              if (delivered || stream.getAudioTracks().length === 0) return
              delivered = true
              stream.removeEventListener('addtrack', deliver)
              el.removeEventListener('playing', deliver)
              if (onAudioStreamRef.current) onAudioStreamRef.current(stream)
            }
            deliver()
            if (!delivered) {
              stream.addEventListener('addtrack', deliver)
              el.addEventListener('playing', deliver)
            }
          } catch { /* recording just won't include this line's voice */ }
        }
        player.onAudioEnd = () => finish(true)
        playerRef.current = player
        azureActiveRef.current = true
        setSpeaking(true)
        const synthesizer = new SpeechSDK.SpeechSynthesizer(config, SpeechSDK.AudioConfig.fromSpeakerOutput(player))
        // Word timings arrive during synthesis (ahead of playback). textOffset
        // is into the SSML, not `text`, so each word is located in `text` by
        // searching forward from the previous one.
        synthesizer.wordBoundary = (_sender, event) => {
          const word = event.text || ''
          if (!word) return
          const idx = text.indexOf(word, boundaryCursor)
          if (idx < 0) return
          boundaryCursor = idx + word.length
          boundaries.push({ atMs: event.audioOffset / 10000, endChar: boundaryCursor }) // ticks (100ns) -> ms
        }
        synthesizer.speakSsmlAsync(
          buildSsml(text, voice, token.tts_style, token.tts_rate),
          (result) => {
            synthesizer.close()
            if (result.reason !== SpeechSDK.ResultReason.SynthesizingAudioCompleted) {
              // eslint-disable-next-line no-console
              console.warn('Azure TTS failed, falling back to browser voice:', result.errorDetails)
              if (generation === generationRef.current) stopAzure()
              finish(false)
            }
            // Success: playback continues; player.onAudioEnd settles it.
          },
          (error) => {
            synthesizer.close()
            // eslint-disable-next-line no-console
            console.warn('Azure TTS error, falling back to browser voice:', error)
            if (generation === generationRef.current) stopAzure()
            finish(false)
          },
        )
      } catch {
        finish(false)
        return
      }
      // Absolute cap in case onAudioEnd never fires - never hang the
      // conversation. Sized generously to the text.
      const hardCap = setTimeout(() => finish(started), Math.min(90000, Math.max(6000, text.length * 150)))
    })

  // Resolves once the line finishes (or fails) speaking — callers await
  // this to know when it's safe to start listening again.
  const speak = useCallback(async (text: string): Promise<void> => {
    if (!text.trim()) return
    const generation = ++generationRef.current
    stopAzure() // never overlap with a previous line
    if (browserSupported) window.speechSynthesis.cancel()
    // Gate the mic straight away - covers the token fetch/synthesis latency.
    azureActiveRef.current = true
    setSpeaking(true)
    const token = await fetchToken()
    if (generation !== generationRef.current) return
    if (token && (await speakAzure(text, token, generation))) return
    if (generation !== generationRef.current) return
    azureActiveRef.current = false
    await speakBrowser(text, generation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserSupported, fetchToken])

  const stop = useCallback(() => {
    generationRef.current++
    stopAzure()
    if (browserSupported) window.speechSynthesis.cancel()
    setSpeaking(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserSupported])

  useEffect(() => () => {
    generationRef.current++
    stopAzure()
    if (browserSupported) window.speechSynthesis.cancel()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserSupported])

  return { speak, stop, speaking, supported }
}
